import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { useVat } from '../../contexts/VatContext';
import toast from 'react-hot-toast';
import { navigationApi, productsApi, API_BASE_URL } from '../../api';
import { notificationApi } from '../../api/notifications';
import logo from "../../assets/logo.avif";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { totalItems, items } = useCart();
  const { user, isAuthenticated, login, register, logout, loading } = useAuth();
  const { vatMode, setVat } = useVat();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTotalCount, setSearchTotalCount] = useState(0);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showCartDropdown, setShowCartDropdown] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '', rememberMe: false });
  const [registerForm, setRegisterForm] = useState({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '', agreeToTerms: false });
  const [isRegistering, setIsRegistering] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [userDropdownTimeout, setUserDropdownTimeout] = useState(null);
  const [productCategories, setProductCategories] = useState([]);
  const [megaMenuData, setMegaMenuData] = useState({});
  const [megaMenuCache, setMegaMenuCache] = useState({}); // Cache for loaded mega menus
  const [loadingMegaMenu, setLoadingMegaMenu] = useState(null); // Track which category is loading
  const [allMegaMenusFetched, setAllMegaMenusFetched] = useState(false); // Track if all mega menus are loaded
  const [isLoadingNav, setIsLoadingNav] = useState(true);
  const [allCategories, setAllCategories] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [authError, setAuthError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notificationsList, setNotificationsList] = useState([]);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [notificationDropdownTimeout, setNotificationDropdownTimeout] = useState(null);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // Forgot Password States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState('email'); // 'email', 'otp'
  const [forgotPasswordForm, setForgotPasswordForm] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [showForgotPasswordNewPassword, setShowForgotPasswordNewPassword] = useState(false);
  const [showForgotPasswordConfirmPassword, setShowForgotPasswordConfirmPassword] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');

  // Fetch unread notification count
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadNotifications(0);
      return;
    }
    try {
      const result = await notificationApi.getUnreadCount();
      if (result.success) {
        setUnreadNotifications(result.data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching notification count:', err);
    }
  }, [isAuthenticated]);

  // Fetch notifications for dropdown
  const fetchNotificationsForDropdown = useCallback(async () => {
    if (!isAuthenticated || loadingNotifications) return;
    setLoadingNotifications(true);
    try {
      const result = await notificationApi.getNotifications({ limit: 5 });
      if (result.success) {
        setNotificationsList(result.data.notifications || []);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  }, [isAuthenticated, loadingNotifications]);

  // Fetch notification count on auth change and periodically
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
    }
  }, [isAuthenticated, fetchUnreadCount]);

  // Fetch header navigation data and all categories
  useEffect(() => {
    const fetchNavigationData = async () => {
      try {
        // Fetch all navigation data in one call
        const navResponse = await navigationApi.getNavigationData();

        if (navResponse.success) {
          const { headerLinks, categories: allCats, brands } = navResponse.data;

          // Transform API data to match existing structure
          const categories = headerLinks;

          const transformedCategories = categories.map(cat => ({
            name: cat.name,
            slug: cat.slug,
            href: cat.href,
            hasDropdown: cat.hasDropdown,
            isSpecial: cat.name === 'Clearance Sale' || cat.name === 'Deals'
          }));

          setProductCategories(transformedCategories);

          // Setup "All Products" mega menu
          setAllCategories(allCats);
          setMegaMenuData(prev => ({
            ...prev,
            'All Products': {
              type: 'all-products',
              categories: allCats
            }
          }));

          // Setup "Brands" mega menu
          const sortedBrands = [...brands].sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setAllBrands(sortedBrands);

          setMegaMenuData(prev => ({
            ...prev,
            'Brands': {
              type: 'brands',
              brands: sortedBrands
            }
          }));
        }
      } catch (error) {
        console.error('Error fetching navigation data:', error);
        // Fallback to static data if API fails
        setProductCategories([
          { name: 'Products', href: '/products', hasDropdown: false }
        ]);
      } finally {
        setIsLoadingNav(false);
      }
    };

    fetchNavigationData();
  }, []);

  // Handle redirect from protected routes - show login modal
  useEffect(() => {
    if (location.state?.showLogin && !isAuthenticated) {
      setShowLoginModal(true);
      if (location.state?.message) {
        toast.error(location.state.message);
      }
      // Clear the state to prevent showing the modal again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, isAuthenticated]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch search results when debounced query changes
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!debouncedSearchQuery.trim()) {
        setSearchResults([]);
        setSearchTotalCount(0);
        setSearchLoading(false);
        return;
      }

      setSearchLoading(true);
      try {
        const response = await productsApi.searchProducts(debouncedSearchQuery, 6);

        if (response.success) {
          setSearchResults(response.data.products);
          setSearchTotalCount(response.data.total);
        }
      } catch (error) {
        console.error('Error fetching search results:', error);
        setSearchResults([]);
        setSearchTotalCount(0);
      } finally {
        setSearchLoading(false);
      }
    };

    fetchSearchResults();
  }, [debouncedSearchQuery]);



  // Prevent body scroll when modal is open
  useEffect(() => {
    const isAnyModalOpen = showLoginModal || showMobileMenu;

    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showLoginModal, showMobileMenu]);

  // Handle click outside for search results
  useEffect(() => {
    const handleClickOutside = (e) => {
      const searchContainer = document.getElementById('search-container');
      if (searchContainer && !searchContainer.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchResults]);

  // Main page navigation - increased spacing for balanced alignment
  const mainPages = [
    { name: 'Products', href: '/products' },
    { name: 'Collections', href: '/collections' },
    { name: 'About Us', href: '/about' }
  ];

  const handleCartClick = () => {
    navigate('/cart');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleContactClick = () => {
    navigate('/contact');
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearchResults(false);
      setShowMobileMenu(false);
      setSearchQuery('');
    }
  };

  const handleSearchItemClick = (href) => {
    navigate(href);
    setShowSearchResults(false);
    setShowMobileMenu(false);
    setSearchQuery('');
  };

  const handleUserClick = () => {
    if (isAuthenticated) {
      navigate('/account');
    } else {
      setShowLoginModal(true);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setIsSubmitting(true);

    const result = await login(loginForm.email, loginForm.password);

    setIsSubmitting(false);

    if (result.success) {
      setShowLoginModal(false);
      setLoginForm({ email: '', password: '', rememberMe: false });
      toast.success(`Welcome back, ${result.user?.firstName || 'User'}!`);

      // Redirect to intended page if user was trying to access a protected route
      if (location.state?.from) {
        navigate(location.state.from);
      }
    } else {
      setAuthError(result.message || 'Login failed. Please try again.');
      toast.error(result.message || 'Login failed. Please try again.');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthError('');

    // Validate passwords match
    if (registerForm.password !== registerForm.confirmPassword) {
      setAuthError('Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(registerForm.password)) {
      setAuthError('Password must be at least 8 characters with uppercase, lowercase, number, and special character');
      return;
    }

    // Validate terms agreement
    if (!registerForm.agreeToTerms) {
      setAuthError('Please agree to the Privacy Policy and Terms & Conditions');
      return;
    }

    setIsSubmitting(true);

    // Split name into firstName and lastName
    const nameParts = registerForm.firstName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || registerForm.lastName || '';

    const result = await register(
      firstName,
      lastName,
      registerForm.email,
      registerForm.password
    );

    setIsSubmitting(false);

    if (result.success) {
      setShowLoginModal(false);
      setRegisterForm({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '', agreeToTerms: false });
      toast.success(`Welcome aboard, ${result.user?.firstName || 'User'}!`);

      // Redirect to intended page if user was trying to access a protected route
      if (location.state?.from) {
        navigate(location.state.from);
      }
    } else {
      setAuthError(result.message || 'Registration failed. Please try again.');
      toast.error(result.message || 'Registration failed. Please try again.');
    }
  };

  const handleLogout = () => {
    logout();
    setShowUserDropdown(false);
    toast.success('Logged out successfully. See you soon!');
    navigate('/');
  };

  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordError('');
    setForgotPasswordSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/website-user/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: forgotPasswordForm.email }),
      });

      const data = await response.json();

      if (response.ok) {
        setForgotPasswordSuccess(data.message || 'OTP sent to your email. Please check your inbox.');
        setForgotPasswordStep('otp');
        toast.success('OTP sent! Check your email.');
      } else {
        if (response.status === 429) {
          setForgotPasswordError('Too many requests. Please try again later.');
        } else {
          setForgotPasswordError(data.message || 'Failed to send OTP. Please try again.');
        }
        toast.error(data.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setForgotPasswordError('Network error. Please check your connection.');
      toast.error('Network error. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordError('');
    setForgotPasswordSuccess('');

    // Validate passwords match
    if (forgotPasswordForm.newPassword !== forgotPasswordForm.confirmPassword) {
      setForgotPasswordError('Passwords do not match');
      setForgotPasswordLoading(false);
      toast.error('Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(forgotPasswordForm.newPassword)) {
      setForgotPasswordError('Password must be at least 8 characters with uppercase, lowercase, number, and special character');
      setForgotPasswordLoading(false);
      toast.error('Password does not meet requirements');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/website-user/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: forgotPasswordForm.email,
          otp: forgotPasswordForm.otp,
          newPassword: forgotPasswordForm.newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setForgotPasswordSuccess('Password reset successful! Redirecting to login...');
        toast.success('Password reset successful!');

        // Reset form and close modal after 2 seconds
        setTimeout(() => {
          handleBackToLogin();
          setShowLoginModal(false);
        }, 2000);
      } else {
        setForgotPasswordError(data.message || 'Failed to reset password. Please try again.');
        toast.error(data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setForgotPasswordError('Network error. Please check your connection.');
      toast.error('Network error. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep('email');
    setForgotPasswordForm({ email: '', otp: '', newPassword: '', confirmPassword: '' });
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
  };

  const isActivePage = (href) => {
    return location.pathname === href || location.pathname.startsWith(href.split('?')[0] + '/');
  };

  const handleVatChange = (newVatMode) => {
    setVat(newVatMode);
  };

  const handleMobileNavClick = (href) => {
    navigate(href);
    setShowMobileMenu(false);
  };

  const handleCategoryHover = async (categoryName) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }

    const timeout = setTimeout(async () => {
      // If it's "All Products" or "Brands", we already have the data
      if (categoryName === 'All Products' || categoryName === 'Brands') {
        setHoveredCategory(categoryName);
        return;
      }

      // Check if we already have this mega menu data cached
      if (megaMenuCache[categoryName]) {
        setMegaMenuData(prev => ({
          ...prev,
          [categoryName]: megaMenuCache[categoryName]
        }));
        setHoveredCategory(categoryName);
        return;
      }

      // If we haven't fetched all mega menus yet, fetch them all at once
      if (!allMegaMenusFetched) {
        setLoadingMegaMenu(categoryName);
        setHoveredCategory(categoryName);

        try {
          // Fetch mega menu data for all categories in parallel
          const fetchPromises = productCategories.map(async (cat) => {
            try {
              const response = await navigationApi.getCategoryMegaMenu(cat.slug);
              if (response.success) {
                return { categoryName: cat.name, data: response.data };
              }
              return null;
            } catch (err) {
              console.error(`Error fetching mega menu for ${cat.name}:`, err);
              return null;
            }
          });

          const results = await Promise.all(fetchPromises);

          // Process and cache all results
          const newCache = {};
          const newMegaMenuData = {};

          results.forEach(result => {
            if (result && result.data) {
              const data = result.data;

              // Build columns in the specified order, filtering out empty ones
              const columnsConfig = [
                {
                  title: data.columns.brands?.title || 'Brands',
                  items: data.columns.brands?.items || [],
                  hasMore: data.columns.brands?.hasMore || false,
                  totalCount: data.columns.brands?.totalCount || 0,
                  type: 'brand'
                },
                {
                  title: data.columns.materials?.title || 'Materials',
                  items: data.columns.materials?.items || [],
                  hasMore: data.columns.materials?.hasMore || false,
                  totalCount: data.columns.materials?.totalCount || 0,
                  type: 'material'
                },
                {
                  title: data.columns.genders?.title || 'Gender',
                  items: data.columns.genders?.items || [],
                  hasMore: data.columns.genders?.hasMore || false,
                  totalCount: data.columns.genders?.totalCount || 0,
                  type: 'gender'
                },
                {
                  title: data.columns.colors?.title || 'Shop by Color',
                  items: data.columns.colors?.items || [],
                  hasMore: data.columns.colors?.hasMore || false,
                  totalCount: data.columns.colors?.totalCount || 0,
                  type: 'color'
                },
                {
                  title: 'Categories',
                  items: data.columns.subCategories?.items || [],
                  hasMore: data.columns.subCategories?.hasMore || false,
                  totalCount: data.columns.subCategories?.totalCount || 0,
                  type: 'subCategory'
                }
              ];

              // Filter out empty columns
              const activeColumns = columnsConfig.filter(col => col.items && col.items.length > 0);

              const transformedMegaMenu = {
                columns: activeColumns,
                banners: []
              };

              newCache[result.categoryName] = transformedMegaMenu;
              newMegaMenuData[result.categoryName] = transformedMegaMenu;
            }
          });

          // Update cache and data
          setMegaMenuCache(prev => ({ ...prev, ...newCache }));
          setMegaMenuData(prev => ({ ...prev, ...newMegaMenuData }));
          setAllMegaMenusFetched(true);

        } catch (error) {
          console.error('Error fetching all mega menus:', error);
        } finally {
          setLoadingMegaMenu(null);
        }
      } else {
        // All menus are already fetched, just show the hovered one
        setHoveredCategory(categoryName);
      }
    }, 150);

    setHoverTimeout(timeout);
  };

  const handleCategoryLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }

    const timeout = setTimeout(() => {
      setHoveredCategory('');
    }, 200);

    setHoverTimeout(timeout);
  };

  const handleDropdownEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
  };

  const handleDropdownLeave = () => {
    const timeout = setTimeout(() => {
      setHoveredCategory('');
    }, 200);

    setHoverTimeout(timeout);
  };

  const getCartSubtotal = () => {
    return items.reduce((total, item) => {
      const itemPrice = item.unitPrice || item.price || 0;
      return total + (itemPrice * item.quantity);
    }, 0).toFixed(2);
  };

  return (
    <>
      {/* Main Header Bar - Static, visible site-wide */}
      <header className="bg-white/95 backdrop-blur-md shadow-sm border-b border-[#E8ECF2]/40 relative z-[100]">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Left Section: Logo */}
            <div className="flex items-center">
              <button
                onClick={handleLogoClick}
                className="flex items-center hover:opacity-90 transition-opacity duration-200 focus:outline-none"
              >
                <img
                  src={logo}
                  alt="Workwear Apparel Logo"
                  className="md:h-14 h-10 w-auto object-contain"
                />
              </button>
            </div>

            {/* Center Section: Search Bar */}
            <div id="search-container" className="hidden lg:flex flex-1 max-w-2xl mx-8 relative">
              <form onSubmit={handleSearchSubmit} className="w-full relative group">
                <div className="relative flex items-center">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSearchResults(true);
                    }}
                    onFocus={() => setShowSearchResults(true)}
                    placeholder="Search our products"
                    className="w-full pl-6 pr-14 py-2 bg-white border-2 border-[#E8ECF2] rounded-full focus:outline-none transition-all text-sm h-9 shadow-sm hover:border-[#011e5c]/30"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  />
                  <button
                    type="submit"
                    className="absolute right-[2px] top-[2px] bottom-[2px] px-6 bg-[#011e5c] text-white rounded-r-full hover:bg-[#01164F] transition-colors flex items-center justify-center border-l border-white/10"
                  >
                    <i className="ri-search-line text-lg"></i>
                  </button>
                </div>

                {/* Search Results Dropdown */}
                {showSearchResults && searchQuery.trim() && (
                  <div className="absolute top-[calc(100%+8px)] left-0 right-0 bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-[#E8ECF2] overflow-hidden z-[1001]">
                    <div className="max-h-[min(480px,70vh)] overflow-y-auto custom-scrollbar">
                      {searchLoading ? (
                        <div className="flex flex-col items-center justify-center py-10">
                          <div className="w-8 h-8 border-3 border-[#011e5c] border-t-transparent rounded-full animate-spin mb-3"></div>
                          <p className="text-gray-500 text-sm">Searching products...</p>
                        </div>
                      ) : searchResults.length > 0 ? (
                        <div className="p-2">
                          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-50 mb-1">
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Products</span>
                            <span className="text-xs text-gray-400">{searchTotalCount} matches</span>
                          </div>
                          {searchResults.map((product) => (
                            <Link
                              key={product.id}
                              to={`/product/${product.slug}`}
                              onClick={() => {
                                setShowSearchResults(false);
                                setSearchQuery('');
                              }}
                              className="flex items-center gap-4 p-3 hover:bg-[#F8FBFF] rounded-xl transition-colors group"
                            >
                              <div className="w-14 h-14 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100 p-1">
                                <img src={product.primaryImage || 'https://via.placeholder.com/300'} alt={product.name} className="w-full h-full object-contain mix-blend-multiply group-hover:scale-110 transition-transform" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-gray-900 group-hover:text-[#011e5c] transition-colors truncate">
                                  {product.name}
                                </h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[#011e5c] font-bold text-sm">£{product.basePrice?.toFixed(2)}</span>
                                  {product.brand && <span className="text-xs text-gray-400">• {product.brand.name}</span>}
                                </div>
                              </div>
                              <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#011e5c] transition-colors"></i>
                            </Link>
                          ))}
                          {searchTotalCount > 6 && (
                            <button
                              onClick={() => handleSearchSubmit()}
                              className="w-full mt-2 py-3 text-center text-sm font-semibold text-[#011e5c] hover:bg-[#F0F7FF] rounded-xl transition-colors border-t border-gray-50"
                            >
                              View all {searchTotalCount} results
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
                          <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                            <i className="ri-search-line text-gray-300 text-2xl"></i>
                          </div>
                          <p className="text-gray-900 font-medium text-sm">No products found</p>
                          <p className="text-gray-500 text-xs mt-1">Try checking your spelling or use more general terms</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Right Section: VAT Toggle + Search + User + Contact + Cart */}
            <div className="flex items-center space-x-1 lg:space-x-2">
              {/* VAT Toggle */}
              <div className="hidden lg:flex bg-gray-100 rounded-full p-1 h-9">
                <button
                  onClick={() => handleVatChange('Inc')}
                  className={`px-4 text-xs rounded-full transition-all duration-200 h-7 flex items-center justify-center font-semibold ${vatMode === 'Inc'
                    ? 'bg-white text-[#01164F] shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                  style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px' }}
                >
                  Inc VAT
                </button>
                <button
                  onClick={() => handleVatChange('Exc')}
                  className={`px-4 text-xs rounded-full transition-all duration-200 h-7 flex items-center justify-center font-semibold ${vatMode === 'Exc'
                    ? 'bg-white text-[#01164F] shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                  style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px' }}
                >
                  Ex VAT
                </button>
              </div>

              {/* Divider - Desktop only */}
              <div className="hidden lg:block h-6 w-px bg-gray-200 mx-2"></div>

              {/* User Profile Icon - Visible on all screens */}
              <div className="relative">
                <button
                  onClick={handleUserClick}
                  onMouseEnter={() => {
                    if (isAuthenticated) {
                      if (userDropdownTimeout) clearTimeout(userDropdownTimeout);
                      setShowUserDropdown(true);
                    }
                  }}
                  onMouseLeave={() => {
                    const timeout = setTimeout(() => {
                      setShowUserDropdown(false);
                    }, 300);
                    setUserDropdownTimeout(timeout);
                  }}
                  className="w-9 h-9 lg:w-10 lg:h-10 flex items-center justify-center text-gray-600 hover:text-[#01164F] hover:bg-gray-100 transition-all duration-200 rounded-xl"
                  title={loading ? 'Loading...' : (isAuthenticated ? user?.firstName || 'User Profile' : 'Sign In')}
                  disabled={loading}
                >
                  {loading ? (
                    <svg className="animate-spin h-5 w-5 text-[#1E2328]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <i className="ri-user-line text-lg lg:text-xl"></i>
                  )}
                </button>

                {/* User Dropdown - Desktop only */}
                {showUserDropdown && isAuthenticated && (
                  <div
                    className="hidden lg:block absolute right-0 top-12 w-56 bg-white border border-gray-100 rounded-xl shadow-xl py-2 z-[200]"
                    onMouseEnter={() => {
                      if (userDropdownTimeout) clearTimeout(userDropdownTimeout);
                      setShowUserDropdown(true);
                    }}
                    onMouseLeave={() => {
                      const timeout = setTimeout(() => {
                        setShowUserDropdown(false);
                      }, 300);
                      setUserDropdownTimeout(timeout);
                    }}
                  >
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-[#1E2328] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-[#6B7380]" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {user?.email}
                      </p>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={() => {
                          navigate('/account');
                          setShowUserDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-[#1E2328] hover:bg-[#F8F9FA]"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <i className="ri-user-line mr-2"></i>
                        My Account
                      </button>
                      <button
                        onClick={() => {
                          navigate('/account/orders');
                          setShowUserDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-[#1E2328] hover:bg-[#F8F9FA]"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <i className="ri-shopping-bag-line mr-2"></i>
                        My Orders
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-[#F8F9FA]"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <i className="ri-logout-box-line mr-2"></i>
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Notifications Icon - Only when logged in */}
              {isAuthenticated && (
                <div className="relative hidden lg:block">
                  <button
                    onClick={() => navigate('/account/notifications')}
                    onMouseEnter={() => {
                      if (notificationDropdownTimeout) clearTimeout(notificationDropdownTimeout);
                      setShowNotificationDropdown(true);
                      fetchNotificationsForDropdown();
                    }}
                    onMouseLeave={() => {
                      const timeout = setTimeout(() => {
                        setShowNotificationDropdown(false);
                      }, 300);
                      setNotificationDropdownTimeout(timeout);
                    }}
                    className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-[#01164F] hover:bg-gray-100 transition-all duration-200 rounded-xl relative"
                    title="Notifications"
                  >
                    <i className="ri-notification-3-line text-xl"></i>
                    {unreadNotifications > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-sm">
                        {unreadNotifications > 99 ? '99+' : unreadNotifications}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {showNotificationDropdown && (
                    <div
                      className="absolute right-0 top-12 w-80 bg-white border border-gray-100 rounded-xl shadow-xl py-2 z-[200]"
                      onMouseEnter={() => {
                        if (notificationDropdownTimeout) clearTimeout(notificationDropdownTimeout);
                        setShowNotificationDropdown(true);
                      }}
                      onMouseLeave={() => {
                        const timeout = setTimeout(() => {
                          setShowNotificationDropdown(false);
                        }, 300);
                        setNotificationDropdownTimeout(timeout);
                      }}
                    >
                      <div className="px-4 py-3 border-b border-[#E8ECF2] flex items-center justify-between">
                        <h3 className="text-[#1E2328] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                          Notifications
                        </h3>
                        {unreadNotifications > 0 && (
                          <span className="text-xs text-[#F3B11A] font-medium">
                            {unreadNotifications} unread
                          </span>
                        )}
                      </div>

                      <div className="max-h-72 overflow-y-auto">
                        {loadingNotifications ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F3B11A]"></div>
                          </div>
                        ) : notificationsList.length > 0 ? (
                          notificationsList.slice(0, 5).map((notification) => (
                            <div
                              key={notification.id}
                              onClick={() => {
                                setShowNotificationDropdown(false);
                                navigate(notification.actionUrl || '/account/notifications');
                              }}
                              className={`px-4 py-3 hover:bg-[#F8F9FA] cursor-pointer border-l-2 transition-colors ${notification.isRead ? 'border-transparent' : 'border-[#F3B11A] bg-[#FFF9E6]/30'
                                }`}
                            >
                              <div className="flex items-start space-x-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${notification.type === 'ORDER' ? 'bg-blue-100 text-blue-600' :
                                  notification.type === 'PROMOTION' ? 'bg-green-100 text-green-600' :
                                    'bg-orange-100 text-orange-600'
                                  }`}>
                                  <i className={`text-sm ${notification.type === 'ORDER' ? 'ri-truck-line' :
                                    notification.type === 'PROMOTION' ? 'ri-percent-line' :
                                      'ri-shield-check-line'
                                    }`}></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-[#1E2328] line-clamp-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                                    {notification.title}
                                  </p>
                                  <p className="text-xs text-[#6B7380] line-clamp-2 mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-[#9CA3AF] mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                                    {new Date(notification.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="py-8 text-center">
                            <div className="w-10 h-10 bg-[#F3B11A]/10 rounded-xl flex items-center justify-center mx-auto mb-2">
                              <i className="ri-notification-off-line text-lg text-[#F3B11A]"></i>
                            </div>
                            <p className="text-sm text-[#6B7380]" style={{ fontFamily: 'Inter, sans-serif' }}>
                              No notifications yet
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="px-4 pt-2 border-t border-[#E8ECF2]">
                        <button
                          onClick={() => {
                            setShowNotificationDropdown(false);
                            navigate('/account/notifications');
                          }}
                          className="w-full text-center text-sm text-[#F3B11A] hover:text-[#E5A010] font-medium py-2 transition-colors"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          View All Notifications
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Contact Icon */}
              <button
                onClick={handleContactClick}
                className="hidden lg:flex w-10 h-10 items-center justify-center text-gray-600 hover:text-[#01164F] hover:bg-gray-100 transition-all duration-200 rounded-xl"
                title="Contact Support"
              >
                <i className="ri-customer-service-2-line text-xl"></i>
              </button>

              {/* Cart with Badge - Visible on all screens */}
              <div className="relative">
                <button
                  onClick={handleCartClick}
                  onMouseEnter={() => setShowCartDropdown(true)}
                  onMouseLeave={() => setShowCartDropdown(false)}
                  className="relative w-9 h-9 lg:w-10 lg:h-10 flex items-center justify-center text-gray-600 hover:text-[#01164F] hover:bg-gray-100 transition-all duration-200 rounded-xl"
                >
                  <i className="ri-shopping-cart-line text-lg lg:text-xl"></i>
                  {totalItems > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-[#F3B11A] text-[#01164F] text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-sm">
                      {totalItems}
                    </span>
                  )}
                </button>

                {/* Cart Dropdown - Desktop only */}
                {showCartDropdown && totalItems > 0 && (
                  <div
                    className="hidden lg:block absolute right-0 top-12 w-80 bg-white border border-gray-100 rounded-xl shadow-xl py-4 z-[200]"
                    onMouseEnter={() => setShowCartDropdown(true)}
                    onMouseLeave={() => setShowCartDropdown(false)}
                  >
                    <div className="px-4 pb-3 border-b border-[#E8ECF2]">
                      <h3 className="text-[#1E2328] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Shopping Cart ({totalItems} items)
                      </h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {items.slice(0, 3).map((item) => {
                        const itemName = item.product?.name || item.deal?.name || item.name || 'Item';
                        const itemImage = item.product?.images?.[0]?.imageUrl || item.deal?.imageUrl || item.image || '';
                        const variantInfo = item.variant ? `${item.variant.color?.name || ''} / ${item.variant.size?.name || ''}` : '';

                        return (
                          <div key={item.id} className="flex items-center space-x-3 px-4 py-3 hover:bg-[#F8F9FA]">
                            <img src={itemImage} alt={itemName} className="w-12 h-12 object-contain rounded bg-gray-100" />
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-[#1E2328] line-clamp-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                                {itemName}
                              </h4>
                              {variantInfo && (
                                <p className="text-xs text-[#9CA3AF] line-clamp-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                                  {variantInfo}
                                </p>
                              )}
                              <p className="text-xs text-[#6B7380]" style={{ fontFamily: 'Inter, sans-serif' }}>
                                Qty: {item.quantity} × £{(item.unitPrice || 0).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="px-4 pt-3 border-t border-[#E8ECF2]">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[#1E2328] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Subtotal:</span>
                        <span className="text-[#1E2328] font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>£{getCartSubtotal()}</span>
                      </div>
                      <button
                        onClick={handleCartClick}
                        className="w-full bg-[#F3B11A] text-[#01164F] py-2 rounded-lg font-medium hover:bg-[#E5A010] transition-colors"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        View Cart
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(true)}
                className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-white bg-gray-100 hover:bg-[#01164F] transition-all rounded-xl active:scale-95"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Sub-Category Navigation Bar */}
      <nav className="hidden lg:block bg-gradient-to-r from-[#01164F] via-[#012060] to-[#01164F] relative z-30 shadow-md">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex items-center justify-center space-x-1 h-12">
            {isLoadingNav ? (
              // Loading skeleton
              <>
                {[...Array(6)].map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="h-6 w-20 bg-white/20 rounded"></div>
                  </div>
                ))}
              </>
            ) : (
              <>
                {/* All Products - First Item */}
                <div
                  className="relative"
                  onMouseEnter={() => handleCategoryHover('All Products')}
                  onMouseLeave={handleCategoryLeave}
                >
                  <Link
                    to="/categories"
                    className="flex items-center space-x-1.5 text-white/90 hover:text-white transition-all duration-200 py-2.5 px-4 relative group text-sm font-medium rounded-lg hover:bg-white/10"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <span>All Products</span>
                    <i className="ri-arrow-down-s-line text-sm opacity-70 group-hover:opacity-100 group-hover:rotate-180 transition-all duration-200"></i>
                  </Link>
                </div>

                {/* Separator */}
                <div className="h-5 w-px bg-white/20"></div>

                {/* Brands - Second Item */}
                <div
                  className="relative"
                  onMouseEnter={() => handleCategoryHover('Brands')}
                  onMouseLeave={handleCategoryLeave}
                >
                  <Link
                    to="/brands"
                    className="flex items-center space-x-1.5 text-white/90 hover:text-white transition-all duration-200 py-2.5 px-4 relative group text-sm font-medium rounded-lg hover:bg-white/10"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <span>Brands</span>
                    <i className="ri-arrow-down-s-line text-sm opacity-70 group-hover:opacity-100 group-hover:rotate-180 transition-all duration-200"></i>
                  </Link>
                </div>

                {/* Separator */}
                <div className="h-5 w-px bg-white/20"></div>

                {/* Individual Categories */}
                {productCategories.map((category, index) => (
                  <div
                    key={index}
                    className="relative"
                    onMouseEnter={() => handleCategoryHover(category.name)}
                    onMouseLeave={handleCategoryLeave}
                  >
                    <Link
                      to={category.href}
                      className={`flex items-center space-x-1.5 text-white/90 hover:text-white transition-all duration-200 py-2.5 px-4 relative group text-sm font-medium rounded-lg hover:bg-white/10 ${isActivePage(category.href) ? 'text-white bg-white/10' : ''
                        }`}
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {category.isSpecial ? (
                        <span className="flex items-center space-x-2">
                          <span>{category.name.replace(' Sale', '')}</span>
                          <span className="bg-[#F3B11A] text-[#01164F] px-2 py-0.5 rounded text-[11px] font-bold">
                            Sale
                          </span>
                        </span>
                      ) : (
                        <span>{category.name}</span>
                      )}
                    </Link>
                  </div>
                ))}
                {/* Separator before Deals */}
                <div className="h-5 w-px bg-white/20"></div>

                {/* View All Bundles Button */}
                <div className="relative">
                  <Link
                    to="/bundles"
                    className="flex items-center ml-2 py-1 px-4 bg-[#F3B11A] text-[#011e5c] rounded-xl font-bold transition-all duration-200 hover:bg-[#E5A010] hover:shadow-lg active:scale-95"
                    style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
                  >
                    <span className="relative flex items-center gap-2">
                      <i className="ri-percent-line text-[#011e5c]"></i>
                      Deals & Bundles
                    </span>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Centered full-width mega menu dropdown */}
        {hoveredCategory && (
          <div
            className="absolute left-1/2 w-full max-w-[1320px] bg-white rounded-2xl border border-gray-100 animate-fade-in-center"
            style={{
              top: '100%',
              marginTop: '12px',
              boxShadow: '0 20px 50px rgba(1, 22, 79, 0.15), 0 5px 15px rgba(0, 0, 0, 0.08)',
              paddingInline: '24px',
              zIndex: 1000,
              transform: 'translateX(-50%)'
            }}
            onMouseEnter={handleDropdownEnter}
            onMouseLeave={handleDropdownLeave}
          >
            {loadingMegaMenu === hoveredCategory ? (
              /* Loading State */
              <div className="px-10 py-20 flex items-center justify-center">
                <div className="text-center">
                  <svg className="animate-spin h-10 w-10 text-[#F3B11A] mx-auto mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-[#6B7380] text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Loading {hoveredCategory}...
                  </p>
                </div>
              </div>
            ) : megaMenuData[hoveredCategory] ? (
              <>
                {/* All Products View */}
                {megaMenuData[hoveredCategory].type === 'all-products' ? (
                  <div className="px-8 py-10">
                    <div className="mb-6 pb-4 border-b border-gray-200">
                      <h2
                        className="text-[#01164F] font-bold text-2xl"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Browse All Categories
                        <span className="ml-3 text-sm font-normal text-gray-500">
                          Find exactly what you need
                        </span>
                      </h2>
                    </div>

                    {/* Alphabetical Grid Layout */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-5">
                      {(() => {
                        // Group categories by first letter
                        const grouped = {};
                        megaMenuData[hoveredCategory].categories.forEach(cat => {
                          const firstLetter = cat.name.charAt(0).toUpperCase();
                          if (!grouped[firstLetter]) {
                            grouped[firstLetter] = [];
                          }
                          // Build href from slug
                          grouped[firstLetter].push({
                            name: cat.name,
                            href: `/categories/${cat.slug}`
                          });
                        });

                        // Sort letters and render
                        return Object.keys(grouped).sort().map(letter => (
                          <div key={letter} className="flex items-start">
                            <div className="flex-shrink-0 mr-3">
                              <div
                                className="w-8 h-8 bg-gradient-to-br from-[#F3B11A] to-[#E5A010] text-[#01164F] rounded-md flex items-center justify-center font-bold text-sm shadow-sm"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                {letter}
                              </div>
                            </div>
                            <div className="flex-1 pt-0.5">
                              <div className="flex flex-wrap items-center gap-x-1">
                                {grouped[letter].map((cat, idx) => (
                                  <span key={idx} className="inline-flex items-center">
                                    <Link
                                      to={cat.href}
                                      className="text-[#333333] hover:text-[#F3B11A] text-sm transition-colors duration-200 hover:underline font-medium"
                                      style={{ fontFamily: 'Inter, sans-serif' }}
                                    >
                                      {cat.name}
                                    </Link>
                                    {idx < grouped[letter].length - 1 && (
                                      <span className="mx-1.5 text-gray-300 text-xs">|</span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>

                    {/* Bottom CTA */}
                    <div className="mt-8 pt-6 border-t-2 border-gray-200">
                      <div className="flex items-center justify-between bg-gradient-to-r from-[#01164F] to-[#01164F]/90 rounded-xl p-6">
                        <div>
                          <h3 className="text-white font-bold text-lg mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                            Can't find what you're looking for?
                          </h3>
                          <p className="text-white/80 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                            Contact our team for personalized assistance
                          </p>
                        </div>
                        <Link
                          to="/contact"
                          className="bg-[#F3B11A] text-[#01164F] px-6 py-3 rounded-lg font-bold hover:bg-[#E5A010] transition-colors flex items-center space-x-2"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          <span>Contact Us</span>
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : megaMenuData[hoveredCategory].type === 'brands' ? (
                  /* Brands View */
                  <div className="px-8 py-10">
                    <div className="mb-6 pb-4 border-b border-gray-200">
                      <h2
                        className="text-[#01164F] font-bold text-2xl"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Browse All Brands
                        <span className="ml-3 text-sm font-normal text-gray-500">
                          Shop by your favorite brands
                        </span>
                      </h2>
                    </div>

                    {/* Alphabetical Grid Layout - show full height, no internal scroll */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-5 pr-2">
                      {(() => {
                        // Group brands by first letter
                        const grouped = {};
                        megaMenuData[hoveredCategory].brands.forEach(brand => {
                          const firstLetter = brand.name.charAt(0).toUpperCase();
                          if (!grouped[firstLetter]) {
                            grouped[firstLetter] = [];
                          }
                          grouped[firstLetter].push({
                            name: brand.name,
                            href: `/products?brand=${brand.slug}`
                          });
                        });

                        // Sort letters and render
                        return Object.keys(grouped).sort().map(letter => (
                          <div key={letter} className="flex items-start">
                            <div className="flex-shrink-0 mr-3">
                              <div
                                className="w-8 h-8 bg-gradient-to-br from-[#F3B11A] to-[#E5A010] text-[#01164F] rounded-md flex items-center justify-center font-bold text-sm shadow-sm"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                {letter}
                              </div>
                            </div>
                            <div className="flex-1 pt-0.5">
                              <div className="flex flex-wrap items-center gap-x-1">
                                {grouped[letter].map((brand, idx) => (
                                  <span key={idx} className="inline-flex items-center">
                                    <Link
                                      to={brand.href}
                                      className="text-[#333333] hover:text-[#F3B11A] text-sm transition-colors duration-200 hover:underline font-medium"
                                      style={{ fontFamily: 'Inter, sans-serif' }}
                                    >
                                      {brand.name}
                                    </Link>
                                    {idx < grouped[letter].length - 1 && (
                                      <span className="mx-1.5 text-gray-300 text-xs">|</span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>

                    {/* Bottom CTA */}
                    <div className="mt-8 pt-6 border-t-2 border-gray-200">
                      <div className="flex items-center justify-between bg-gradient-to-r from-[#01164F] to-[#01164F]/90 rounded-xl p-6">
                        <div>
                          <h3 className="text-white font-bold text-lg mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                            Looking for a specific brand?
                          </h3>
                          <p className="text-white/80 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                            Contact us if you can't find what you're looking for
                          </p>
                        </div>
                        <Link
                          to="/contact"
                          className="bg-[#F3B11A] text-[#01164F] px-6 py-3 rounded-lg font-bold hover:bg-[#E5A010] transition-colors flex items-center space-x-2"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          <span>Contact Us</span>
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Regular Category Mega Menu */
                  <div className="px-10 py-10">
                    {/* Category header */}
                    <div className="mb-6 pb-4 border-b border-gray-200">
                      <h2
                        className="text-[#01164F] font-bold text-xl"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {hoveredCategory}
                        <span className="ml-3 text-sm font-normal text-gray-500">
                          Explore our collection
                        </span>
                      </h2>
                    </div>

                    {/* Multi-column layout */}
                    <div className="grid grid-cols-4 gap-10 mb-8">
                      {megaMenuData[hoveredCategory].columns.map((column, colIndex) => (
                        <div key={colIndex} className="relative">
                          <div className="flex items-center mb-4 pb-2 border-b-2 border-[#F3B11A]">
                            <h3
                              className="text-[#01164F] font-bold uppercase tracking-wide"
                              style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', letterSpacing: '0.5px' }}
                            >
                              {column.title}
                            </h3>
                          </div>
                          <ul className="space-y-2.5">
                            {column.items.map((item, itemIndex) => {
                              // Handle different item types

                              const itemName = item.name;
                              const itemSlug = item.slug || item.name.toLowerCase().replace(/\s+/g, '-');

                              // Build the href based on the column type
                              let href = '';

                              // Find the category slug for the current category
                              const currentCategory = productCategories.find(cat => cat.name === hoveredCategory);
                              const categorySlug = currentCategory?.slug || hoveredCategory.toLowerCase().replace(/\s+/g, '-');

                              if (column.type === 'subCategory') {
                                // Link to subcategory products page
                                href = `/categories/${categorySlug}/${itemSlug}/products`;
                              } else {
                                // For other types (gender, material, brand, color), use old products page with query params
                                href = currentCategory?.href || '/products';

                                if (column.type === 'gender') {
                                  href += `${href.includes('?') ? '&' : '?'}gender=${itemSlug}`;
                                } else if (column.type === 'material') {
                                  href += `${href.includes('?') ? '&' : '?'}material=${itemSlug}`;
                                } else if (column.type === 'brand') {
                                  href += `${href.includes('?') ? '&' : '?'}brand=${itemSlug}`;
                                } else if (column.type === 'color') {
                                  href += `${href.includes('?') ? '&' : '?'}color=${itemSlug}`;
                                }
                              }

                              return (
                                <li key={itemIndex}>
                                  <Link
                                    to={href}
                                    className="flex items-center group text-[#333333] hover:text-[#F3B11A] transition-all duration-200 py-2 px-3 hover:bg-[#FFF9E6] rounded-lg"
                                    style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
                                  >
                                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full mr-3 group-hover:bg-[#F3B11A] transition-colors"></span>
                                    <span className="group-hover:translate-x-1 transition-transform duration-200">{itemName}</span>
                                  </Link>
                                </li>
                              );
                            })}
                            {column.hasMore && (
                              <li className="pt-2">
                                <Link
                                  to={`${productCategories.find(cat => cat.name === hoveredCategory)?.href}&type=${column.type}`}
                                  className="flex items-center text-[#F3B11A] hover:text-[#E5A010] font-semibold transition-colors py-2 px-3 rounded-lg hover:bg-[#FFF9E6]"
                                  style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px' }}
                                >
                                  <i className="ri-arrow-right-line mr-2"></i>
                                  View All ({column.totalCount})
                                </Link>
                              </li>
                            )}
                          </ul>
                        </div>
                      ))}
                    </div>

                    {/* Bottom banner row */}
                    {megaMenuData[hoveredCategory].banners && megaMenuData[hoveredCategory].banners.length > 0 && (
                      <div className="grid grid-cols-3 gap-6 border-t-2 border-gray-200 pt-8 mt-6">
                        {megaMenuData[hoveredCategory].banners.map((banner, bannerIndex) => (
                          <div
                            key={bannerIndex}
                            className="relative rounded-xl overflow-hidden group cursor-pointer transform transition-all duration-300 hover:scale-[1.05] shadow-md hover:shadow-2xl"
                          >
                            <img
                              src={banner.image}
                              alt={banner.title}
                              className="w-full h-32 object-contain transition-transform duration-300 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#01164F]/80 via-[#01164F]/40 to-transparent"></div>
                            <div className="absolute inset-0 bg-[#F3B11A]/0 group-hover:bg-[#F3B11A]/10 transition-colors duration-300"></div>
                            <div className="absolute bottom-0 left-0 right-0 p-4">
                              <h4
                                className="text-white font-bold text-base mb-1 transform transition-transform duration-300 group-hover:translate-y-[-2px]"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                {banner.title}
                              </h4>
                              <p
                                className="text-white/90 text-sm flex items-center"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                {banner.subtitle}
                                <i className="ri-arrow-right-line ml-2 transform transition-transform duration-300 group-hover:translate-x-2"></i>
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </nav>

      {/* Login/Register Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
          <div className="bg-[#FEFEFE] rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#01164F]" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {showForgotPassword ? (forgotPasswordStep === 'email' ? 'Reset Password' : 'Verify OTP') : (isRegistering ? 'Create Account' : 'Sign In')}
                </h2>
                <button
                  onClick={() => {
                    setShowLoginModal(false);
                    setAuthError('');
                    setIsRegistering(false);
                    setShowForgotPassword(false);
                    setForgotPasswordStep('email');
                    setForgotPasswordError('');
                    setForgotPasswordSuccess('');
                  }}
                  className="text-[#6B7380] hover:text-[#1E2328] p-1"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {authError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {authError}
                </div>
              )}

              {forgotPasswordError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {forgotPasswordError}
                </div>
              )}

              {forgotPasswordSuccess && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                  {forgotPasswordSuccess}
                </div>
              )}

              {showForgotPassword ? (
                forgotPasswordStep === 'email' ? (
                  <form onSubmit={handleForgotPasswordRequest} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1E2328] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={forgotPasswordForm.email}
                        onChange={(e) => setForgotPasswordForm({ ...forgotPasswordForm, email: e.target.value })}
                        className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B11A]"
                        placeholder="Enter your email address"
                        required
                      />
                    </div>
                    <p className="text-xs text-[#6B7380]" style={{ fontFamily: 'Inter, sans-serif' }}>
                      We'll send you a one-time password (OTP) to reset your password.
                    </p>
                    <button
                      type="submit"
                      disabled={forgotPasswordLoading}
                      className="w-full bg-[#F3B11A] text-[#01164F] py-2 rounded-lg font-medium hover:bg-[#E5A010] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {forgotPasswordLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#01164F]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending OTP...
                        </>
                      ) : (
                        'Send OTP'
                      )}
                    </button>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={handleBackToLogin}
                        className="text-sm text-[#F3B11A] hover:underline"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        ← Back to Sign In
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1E2328] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={forgotPasswordForm.email}
                        className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg bg-gray-50"
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1E2328] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                        OTP Code
                      </label>
                      <input
                        type="text"
                        value={forgotPasswordForm.otp}
                        onChange={(e) => setForgotPasswordForm({ ...forgotPasswordForm, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                        className="w-full px-3 py-2 border border-[#E8ECF2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B11A] text-center text-2xl tracking-widest"
                        placeholder="000000"
                        maxLength="6"
                        required
                      />
                      <p className="text-xs text-[#6B7380] mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Enter the 6-digit code sent to your email
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1E2328] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showForgotPasswordNewPassword ? "text" : "password"}
                          value={forgotPasswordForm.newPassword}
                          onChange={(e) => setForgotPasswordForm({ ...forgotPasswordForm, newPassword: e.target.value })}
                          className="w-full px-3 py-2 pr-10 border border-[#E8ECF2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B11A]"
                          placeholder="Enter new password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowForgotPasswordNewPassword(!showForgotPasswordNewPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6B7380] hover:text-[#1E2328]"
                        >
                          <i className={showForgotPasswordNewPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1E2328] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showForgotPasswordConfirmPassword ? "text" : "password"}
                          value={forgotPasswordForm.confirmPassword}
                          onChange={(e) => setForgotPasswordForm({ ...forgotPasswordForm, confirmPassword: e.target.value })}
                          className="w-full px-3 py-2 pr-10 border border-[#E8ECF2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B11A]"
                          placeholder="Confirm new password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowForgotPasswordConfirmPassword(!showForgotPasswordConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6B7380] hover:text-[#1E2328]"
                        >
                          <i className={showForgotPasswordConfirmPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                        </button>
                      </div>
                      <p className="text-xs text-[#6B7380] mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Must be at least 8 characters with uppercase, lowercase, number, and special character
                      </p>
                    </div>
                    <button
                      type="submit"
                      disabled={forgotPasswordLoading}
                      className="w-full bg-[#F3B11A] text-[#01164F] py-2 rounded-lg font-medium hover:bg-[#E5A010] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {forgotPasswordLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#01164F]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Resetting Password...
                        </>
                      ) : (
                        'Reset Password'
                      )}
                    </button>
                    <div className="text-center space-y-2">
                      <button
                        type="button"
                        onClick={() => {
                          setForgotPasswordStep('email');
                          setForgotPasswordForm({ ...forgotPasswordForm, otp: '', newPassword: '', confirmPassword: '' });
                          setForgotPasswordError('');
                        }}
                        className="text-sm text-[#6B7380] hover:text-[#F3B11A]"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Didn't receive OTP? Resend
                      </button>
                      <div>
                        <button
                          type="button"
                          onClick={handleBackToLogin}
                          className="text-sm text-[#F3B11A] hover:underline"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          ← Back to Sign In
                        </button>
                      </div>
                    </div>
                  </form>
                )
              ) : !isRegistering ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1E2328] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-[#D1D5DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B11A]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1E2328] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showLoginPassword ? "text" : "password"}
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-[#D1D5DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B11A]"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6B7380] hover:text-[#1E2328]"
                      >
                        <i className={showLoginPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setAuthError('');
                      }}
                      className="text-sm text-[#F3B11A] hover:underline"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#F3B11A] text-[#01164F] py-2 rounded-lg font-medium hover:bg-[#E5A010] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#01164F]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Signing In...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                  <div className="text-center">
                    <span className="text-sm text-[#6B7380]" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Don't have an account?{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setIsRegistering(true);
                          setAuthError('');
                        }}
                        className="text-[#F3B11A] hover:underline"
                      >
                        Create one
                      </button>
                    </span>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1E2328] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      First Name
                    </label>
                    <input
                      type="text"
                      value={registerForm.firstName}
                      onChange={(e) => setRegisterForm({ ...registerForm, firstName: e.target.value })}
                      className="w-full px-3 py-2 border border-[#D1D5DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B11A]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1E2328] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={registerForm.lastName}
                      onChange={(e) => setRegisterForm({ ...registerForm, lastName: e.target.value })}
                      className="w-full px-3 py-2 border border-[#D1D5DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B11A]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1E2328] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-[#D1D5DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B11A]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1E2328] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showRegisterPassword ? "text" : "password"}
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-[#D1D5DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B11A]"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6B7380] hover:text-[#1E2328]"
                      >
                        <i className={showRegisterPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                      </button>
                    </div>
                    <p className="text-xs text-[#6B7380] mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Must be at least 8 characters with uppercase, lowercase, number, and special character
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1E2328] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showRegisterConfirmPassword ? "text" : "password"}
                        value={registerForm.confirmPassword}
                        onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-[#D1D5DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B11A]"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegisterConfirmPassword(!showRegisterConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6B7380] hover:text-[#1E2328]"
                      >
                        <i className={showRegisterConfirmPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                      </button>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="agreeToTerms"
                      checked={registerForm.agreeToTerms}
                      onChange={(e) => setRegisterForm({ ...registerForm, agreeToTerms: e.target.checked })}
                      className="mt-1 mr-2 w-4 h-4 text-[#F3B11A] border-[#D1D5DB] rounded focus:ring-[#F3B11A]"
                    />
                    <label htmlFor="agreeToTerms" className="text-sm text-[#6B7380]" style={{ fontFamily: 'Inter, sans-serif' }}>
                      I agree to the{' '}
                      <Link to="/privacy-policy" className="text-[#F3B11A] hover:underline" onClick={(e) => e.stopPropagation()}>Privacy Policy</Link>
                      {' '}and{' '}
                      <Link to="/terms-and-condition" className="text-[#F3B11A] hover:underline" onClick={(e) => e.stopPropagation()}>Terms & Conditions</Link>
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#F3B11A] text-[#01164F] py-2 rounded-lg font-medium hover:bg-[#E5A010] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#01164F]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating Account...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </button>
                  <div className="text-center">
                    <span className="text-sm text-[#6B7380]" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setIsRegistering(false);
                          setAuthError('');
                        }}
                        className="text-[#F3B11A] hover:underline"
                      >
                        Sign in
                      </button>
                    </span>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-[150] lg:hidden">
          {/* Backdrop - Click to close */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
            onClick={() => setShowMobileMenu(false)}
          />

          {/* Menu Panel */}
          <div className="fixed right-0 top-0 h-full w-[280px] max-w-[80vw] bg-white shadow-2xl overflow-hidden animate-slide-in-right flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-[#01164F] to-[#012060]">
              <h2 className="text-base font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>Menu</h2>
              <button
                onClick={() => setShowMobileMenu(false)}
                className="text-white/80 hover:text-white w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-all active:scale-95"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              <div className="p-3">
                {/* Mobile Search Bar */}
                <div className="mb-4 relative">
                  <form onSubmit={handleSearchSubmit} className="relative z-10">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowSearchResults(true);
                      }}
                      onFocus={() => setShowSearchResults(true)}
                      placeholder="Search our products"
                      className="w-full pl-4 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none text-sm"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                    <button
                      type="submit"
                      className="absolute right-0 top-0 bottom-0 px-3 text-gray-400"
                    >
                      <i className="ri-search-line text-lg"></i>
                    </button>
                  </form>

                  {/* Mobile Search Results Dropdown */}
                  {showSearchResults && searchQuery.trim() && (
                    <div className="absolute top-full left-0 right-0 bg-white shadow-xl border border-gray-100 mt-2 rounded-xl z-[160] max-h-[300px] overflow-y-auto">
                      {searchLoading ? (
                        <div className="p-4 text-center">
                          <div className="inline-block w-4 h-4 border-2 border-[#011e5c] border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      ) : searchResults.length > 0 ? (
                        <div className="p-1">
                          {searchResults.map((product) => (
                            <button
                              key={product.id}
                              onClick={() => handleSearchItemClick(`/product/${product.slug}`)}
                              className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg text-left transition-colors"
                            >
                              <div className="w-10 h-10 flex-shrink-0 bg-gray-50 rounded p-1">
                                <img src={product.primaryImage} alt="" className="w-full h-full object-contain" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-gray-900 truncate">{product.name}</p>
                                <p className="text-[10px] text-[#011e5c] font-bold">£{product.basePrice?.toFixed(2)}</p>
                              </div>
                            </button>
                          ))}
                          {searchTotalCount > 6 && (
                            <button
                              onClick={() => handleSearchSubmit()}
                              className="w-full py-2 text-center text-[10px] font-bold text-[#011e5c] uppercase tracking-wider"
                            >
                              View all results
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="p-4 text-center text-xs text-gray-500">No products found</div>
                      )}
                    </div>
                  )}
                </div>
                {/* Mobile VAT Toggle */}
                <div className="mb-3 p-2.5 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex bg-white rounded-lg p-0.5 h-8 border border-gray-200">
                    <button
                      onClick={() => handleVatChange('Inc')}
                      className={`flex-1 text-xs rounded-md transition-all duration-200 flex items-center justify-center font-semibold ${vatMode === 'Inc'
                        ? 'bg-[#01164F] text-white'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Inc VAT
                    </button>
                    <button
                      onClick={() => handleVatChange('Exc')}
                      className={`flex-1 text-xs rounded-md transition-all duration-200 flex items-center justify-center font-semibold ${vatMode === 'Exc'
                        ? 'bg-[#01164F] text-white'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Ex VAT
                    </button>
                  </div>
                </div>

                <nav className="space-y-0.5">
                  {/* Main Navigation */}
                  {/* <div className="mb-2">
                    {mainPages.map((page, index) => (
                      <button
                        key={index}
                        onClick={() => handleMobileNavClick(page.href)}
                        className="flex items-center gap-2.5 w-full text-left text-gray-700 hover:text-[#01164F] py-2 px-2.5 rounded-lg hover:bg-[#01164F]/5 text-sm font-medium transition-all active:scale-[0.98]"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <i className={`${page.name === 'Home' ? 'ri-home-4-line' :
                          page.name === 'Products' ? 'ri-shopping-bag-line' :
                            page.name === 'About' ? 'ri-information-line' :
                              page.name === 'Contact' ? 'ri-phone-line' :
                                'ri-arrow-right-s-line'
                          } text-base text-[#01164F]/50`}></i>
                        <span>{page.name}</span>
                      </button>
                    ))}
                  </div> */}

                  {/* Deals & Bundles Link */}
                  <button
                    onClick={() => handleMobileNavClick('/bundles')}
                    className="flex items-center gap-2.5 w-full text-left bg-gradient-to-r from-[#01164F] to-[#0a2a6e] text-white py-2.5 px-3 rounded-lg text-sm font-semibold transition-all hover:shadow-md active:scale-[0.98] mb-2"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <i className="ri-percent-line text-[#F3B11A]"></i>
                    <span>Deals & Bundles</span>
                    <i className="ri-arrow-right-s-line ml-auto opacity-70"></i>
                  </button>

                  {/* Categories Section */}
                  <div className="border-t border-gray-100 pt-2">
                    <h3 className="text-[10px] font-bold text-gray-400 mb-1 uppercase tracking-wider px-2.5" style={{ fontFamily: 'Inter, sans-serif' }}>Categories</h3>
                    {productCategories.map((category, index) => (
                      <button
                        key={index}
                        onClick={() => handleMobileNavClick(category.href)}
                        className="flex items-center gap-2.5 w-full text-left text-gray-700 hover:text-[#01164F] py-2 px-2.5 rounded-lg hover:bg-[#01164F]/5 text-sm font-medium transition-all active:scale-[0.98]"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <i className="ri-t-shirt-line text-base text-gray-400"></i>
                        <span>{category.name}</span>
                      </button>
                    ))}
                  </div>

                  {/* Account Section */}
                  <div className="border-t border-gray-100 pt-2 mt-2">
                    <h3 className="text-[10px] font-bold text-gray-400 mb-1 uppercase tracking-wider px-2.5" style={{ fontFamily: 'Inter, sans-serif' }}>Account</h3>
                    <button
                      onClick={() => {
                        setShowMobileMenu(false);
                        handleUserClick();
                      }}
                      className="flex items-center gap-2.5 w-full text-left text-gray-700 hover:text-[#01164F] py-2 px-2.5 rounded-lg hover:bg-[#01164F]/5 text-sm font-medium transition-all active:scale-[0.98]"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <i className={`${isAuthenticated ? 'ri-user-3-line' : 'ri-login-box-line'} text-base text-gray-400`}></i>
                      <span>{isAuthenticated ? 'My Account' : 'Sign In'}</span>
                    </button>

                    {isAuthenticated && (
                      <button
                        onClick={() => {
                          setShowMobileMenu(false);
                          navigate('/account/notifications');
                        }}
                        className="flex items-center gap-2.5 w-full text-left text-gray-700 hover:text-[#01164F] py-2 px-2.5 rounded-lg hover:bg-[#01164F]/5 text-sm font-medium transition-all active:scale-[0.98]"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <i className="ri-notification-3-line text-base text-gray-400"></i>
                        <span>Notifications</span>
                        {unreadNotifications > 0 && (
                          <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-4 h-4 flex items-center justify-center px-1">
                            {unreadNotifications > 99 ? '99+' : unreadNotifications}
                          </span>
                        )}
                      </button>
                    )}

                    <button
                      onClick={() => {
                        setShowMobileMenu(false);
                        handleContactClick();
                      }}
                      className="flex items-center gap-2.5 w-full text-left text-gray-700 hover:text-[#01164F] py-2 px-2.5 rounded-lg hover:bg-[#01164F]/5 text-sm font-medium transition-all active:scale-[0.98]"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <i className="ri-customer-service-2-line text-base text-gray-400"></i>
                      <span>Contact Us</span>
                    </button>
                  </div>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fade-in-center {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
        
        .animate-fade-in-center {
          animation: fade-in-center 200ms ease-out forwards;
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .animate-fade-in {
          animation: fade-in 250ms ease-out forwards;
        }
        
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 300ms cubic-bezier(0.32, 0.72, 0, 1) forwards;
        }
      `}</style>
    </>
  );
}
